'use client'

import * as React from 'react'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'

import { cn } from '@repo/ui/lib/utils'
import { Button } from '@repo/ui/components/ui/button'
import { DrawerPortalContainerBoundary } from '@repo/ui/components/ui/drawer-portal-context'
import { XIcon } from 'lucide-react'

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} data-sentry-element="DialogPrimitive.Root" data-sentry-component="Dialog" data-sentry-source-file="dialog.tsx" />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} data-sentry-element="DialogPrimitive.Close" data-sentry-component="DialogClose" data-sentry-source-file="dialog.tsx" />
}

function DialogContent({
  children,
  className,
  showCloseButton = true,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean
}) {
  return (
    <DialogPortal data-sentry-element="DialogPortal" data-sentry-component="DialogContent" data-sentry-source-file="dialog.tsx">
      <DialogOverlay className={'z-61'} data-sentry-element="DialogOverlay" data-sentry-source-file="dialog.tsx" />
      <DialogPrimitive.Popup
        className={cn(
          'fixed top-1/2 left-1/2 z-70 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl bg-popover p-4 text-sm text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 sm:max-w-sm',
          className,
        )}
        data-slot="dialog-content"
        {...props}
      data-sentry-element="DialogPrimitive.Popup" data-sentry-source-file="dialog.tsx">
        <DrawerPortalContainerBoundary data-sentry-element="DrawerPortalContainerBoundary" data-sentry-source-file="dialog.tsx">{children}</DrawerPortalContainerBoundary>
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={<Button className="absolute top-2 right-2" size="icon-sm" variant="ghost" />}
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      className={cn(
        'text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
        className,
      )}
      data-slot="dialog-description"
      {...props}
    data-sentry-element="DialogPrimitive.Description" data-sentry-component="DialogDescription" data-sentry-source-file="dialog.tsx" />
  )
}

function DialogFooter({
  children,
  className,
  showCloseButton = false,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      className={cn(
        '-mx-4 -mb-4 flex flex-col-reverse gap-2 rounded-b-xl border-t bg-muted/50 p-4 sm:flex-row sm:justify-end',
        className,
      )}
      data-slot="dialog-footer"
      {...props}
    data-sentry-component="DialogFooter" data-sentry-source-file="dialog.tsx">
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>Close</DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div className={cn('flex flex-col gap-2', className)} data-slot="dialog-header" {...props} data-sentry-component="DialogHeader" data-sentry-source-file="dialog.tsx" />
  )
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      className={cn(
        'fixed inset-0 isolate z-70 bg-black/10 duration-100 data-closed:animate-out data-closed:fade-out-0 data-open:animate-in data-open:fade-in-0 supports-backdrop-filter:backdrop-blur-xs',
        className,
      )}
      data-slot="dialog-overlay"
      {...props}
    data-sentry-element="DialogPrimitive.Backdrop" data-sentry-component="DialogOverlay" data-sentry-source-file="dialog.tsx" />
  )
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} data-sentry-element="DialogPrimitive.Portal" data-sentry-component="DialogPortal" data-sentry-source-file="dialog.tsx" />
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      className={cn('text-base leading-none font-medium', className)}
      data-slot="dialog-title"
      {...props}
    data-sentry-element="DialogPrimitive.Title" data-sentry-component="DialogTitle" data-sentry-source-file="dialog.tsx" />
  )
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} data-sentry-element="DialogPrimitive.Trigger" data-sentry-component="DialogTrigger" data-sentry-source-file="dialog.tsx" />
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
